/**
 * Auto-init for the two accordion patterns legacy landings still ship as raw
 * markup. The web component strips inline <script> tags from manifest HTML for
 * security, so any accordion that needs JS has to be wired here, inside our
 * trusted runtime, against well-known class-name conventions.
 *
 * Two patterns surface across every legacy landing:
 *
 *   - `.faq__btn` + `.faq__answer` inside `.faq__accordion-item` (universal)
 *   - `.treatments__btn` + `.treatments__content` inside
 *     `.treatments__accordion-item` (contraception only)
 *
 * Every click toggles the `show` class on both the button and the matching
 * content within the same item. Optional chaining on the wrapper traversal
 * means missing markup silently no-ops instead of throwing. Idempotent — a
 * second init against the same root will not double-wire any button.
 *
 * No-op when no matching markup is present — safe to call against every
 * shadow root.
 */

const WIRED_ATTR = 'data-evaro-accordion-wired'

interface AccordionPattern {
  buttonSelector: string
  itemSelector: string
  contentSelector: string
  toggleClass: string
}

const PATTERNS: AccordionPattern[] = [
  {
    buttonSelector: '.faq__btn',
    itemSelector: '.faq__accordion-item',
    contentSelector: '.faq__answer',
    toggleClass: 'show',
  },
  {
    buttonSelector: '.treatments__btn',
    itemSelector: '.treatments__accordion-item',
    contentSelector: '.treatments__content',
    toggleClass: 'show',
  },
]

export function initLegacyAccordions(root: ShadowRoot | Element): void {
  for (const pattern of PATTERNS) wirePattern(root, pattern)
}

function wirePattern(root: ShadowRoot | Element, pattern: AccordionPattern): void {
  const buttons = Array.from(root.querySelectorAll(pattern.buttonSelector)) as HTMLElement[]
  if (buttons.length === 0) return
  for (const button of buttons) {
    if (button.getAttribute(WIRED_ATTR) === '1') continue
    button.setAttribute(WIRED_ATTR, '1')
    button.addEventListener('click', () => {
      button.classList.toggle(pattern.toggleClass)
      const content = button.closest(pattern.itemSelector)?.querySelector(pattern.contentSelector)
      content?.classList.toggle(pattern.toggleClass)
      button.setAttribute(
        'aria-expanded',
        String(content?.classList.contains(pattern.toggleClass) ?? false),
      )
    })
  }
}
